import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BeakerIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  LinkIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  PlusIcon,
  ServerStackIcon,
  ArrowUpTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { ecosystemService } from '../services/ecosystemService';
import { agentInstanceService } from '../services/agentService';
import ClientSelectModal from '../components/chatbot/tabs/ConnectTab/ClientSelectModal';
import { renderChatMarkdown } from '../utility/renderMarkdownText';
import { getUser } from '../lib/storage';
import { Tabs } from '../components/ui/Tabs';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../components/ui/Field';
import { Overlay } from '../components/ui/Overlay';
import { Surface } from '../components/ui/Surface';

// ── constants ────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

const ENTRY_TYPE_LABELS: Record<string, string> = {
  text: 'Text (Chat)',
  'inbound-voice': 'Inbound Voice',
  'outbound-voice': 'Outbound Voice',
};

const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
};

const agentLabel = (inst: any) => inst?.name || inst?.code || inst?.id || 'Unknown';

const copyText = async (text: string, label: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  } catch {
    toast.error('Failed to copy');
  }
};

// ── Tab IDs ──────────────────────────────────────────────────────────────────
type TabId = 'setup' | 'entry-agents' | 'functional-agents' | 'playground' | 'connect' | 'conversations';

const TABS: { id: TabId; label: string; icon: React.ComponentType<any> }[] = [
  { id: 'setup',              label: 'Setup',             icon: Cog6ToothIcon },
  { id: 'entry-agents',      label: 'Entry Agents',       icon: ArrowUpTrayIcon },
  { id: 'functional-agents', label: 'Functional Agents',  icon: ServerStackIcon },
  { id: 'playground',        label: 'Playground',         icon: BeakerIcon },
  { id: 'connect',           label: 'Connect',            icon: LinkIcon },
  { id: 'conversations',     label: 'Conversations',      icon: ChatBubbleLeftRightIcon },
];

// ── Setup Tab ────────────────────────────────────────────────────────────────
const SetupTab: React.FC<{
  name: string; setName: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  state: string; setState: (v: any) => void;
  isActive: boolean; setIsActive: (v: boolean) => void;
  onSave: () => void; saving: boolean; isNew: boolean; hasSavedId: boolean;
}> = ({ name, setName, description, setDescription, state, setState, isActive, setIsActive, onSave, saving, isNew, hasSavedId }) => (
  <div className="p-6 space-y-5 max-w-2xl">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
      <TextField
        type="text" value={name} onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Customer Support Ecosystem"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
      <TextArea
        rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="Optional description..."
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
      />
    </div>
    <div className="flex items-center gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
        <SelectField value={state} onChange={(e) => setState(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </SelectField>
      </div>
      <div className="flex items-center gap-2 mt-5">
        <Checkbox id="isActive" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
        <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Active</label>
      </div>
    </div>
    <div className="pt-2">
      <Button tone="blue" onClick={onSave} disabled={saving}
        className="px-5 py-2 text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
        {saving ? 'Saving...' : isNew && !hasSavedId ? 'Create Ecosystem' : 'Save Changes'}
      </Button>
    </div>
    <div className="mt-4 bg-blue-50 rounded-xl border border-blue-200 p-4 space-y-2">
      <h3 className="text-sm font-semibold text-blue-700">Layer 2 — Orchestrator (Built-in)</h3>
      <p className="text-xs text-blue-600">
        Uses RAG-based intent detection to route each message to the most relevant functional agent automatically.
      </p>
    </div>
  </div>
);

// ── Entry Agents Tab ──────────────────────────────────────────────────────────
const EntryAgentsTab: React.FC<{
  savedId: string | null;
  entryMappings: any[];
  agentInstances: any[];
  onAdd: (id: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  onReload: () => Promise<void>;
  navigate: (path: string) => void;
}> = ({ savedId, entryMappings, agentInstances, onAdd, onRemove, onReload, navigate }) => {
  const [selectedId, setSelectedId] = useState('');
  const [adding, setAdding] = useState(false);
  const [voiceModalMapping, setVoiceModalMapping] = useState<any>(null);
  const ENTRY_TYPES = new Set(['text', 'inbound-voice', 'outbound-voice']);
  const used = new Set(entryMappings.map((m) => m.agentInstanceId));
  const available = agentInstances.filter((a) => !used.has(a.id) && ENTRY_TYPES.has(a.metadata?.entryType || ''));
  const webhookBase = process.env.REACT_APP_CORE_MS_WEBHOOK_BASE_URL || API_BASE;

  const handleAdd = async () => {
    if (!selectedId) { toast.error('Select an agent instance'); return; }
    setAdding(true);
    await onAdd(selectedId);
    setSelectedId('');
    setAdding(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Layer 1 — Entry Agents</h2>
        <p className="text-xs text-gray-400 mt-1">
          Receive incoming interactions and pass them to the orchestrator. Entry type is defined on the agent itself.
          Voice agents must have Twilio configured — use the Voice Config tab in the agent's own config page.
        </p>
      </div>

      {entryMappings.length > 0 ? (
        <ul className="divide-y divide-gray-100 bg-white rounded-xl border border-gray-200">
          {entryMappings.map((m) => {
            const inst = m.agentInstance || agentInstances.find((a) => a.id === m.agentInstanceId);
            const et = m.entryType || inst?.metadata?.entryType || 'text';
            const isVoice = et === 'inbound-voice' || et === 'outbound-voice';
            const hasTwilio = !!inst?.metadata?.twilioConfig?.accountSid;
            const hasPhone = !!(m.config as any)?.twilioPhoneNumber;
            return (
              <li key={m.id} className="flex items-center justify-between px-4 py-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-800">{agentLabel(inst)}</span>
                  <Badge className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                    {ENTRY_TYPE_LABELS[et] || et}
                  </Badge>
                  {isVoice && (
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${hasTwilio ? 'bg-green-50 text-green-600 border-green-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                      {hasTwilio ? 'Credentials set' : 'Credentials missing'}
                    </span>
                  )}
                  {isVoice && (
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${hasPhone ? 'bg-green-50 text-green-600 border-green-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                      {hasPhone ? `Phone: ${(m.config as any).twilioPhoneNumber}` : 'Phone not set'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isVoice && (
                    <Button tone="custom"
                      onClick={() => navigate(`/agents/${m.agentInstanceId}/config`)}
                      className="text-xs px-3 py-1.5 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1"
                    >
                      <PhoneIcon className="h-3 w-3" /> {hasTwilio ? 'Edit Credentials' : 'Set Credentials'}
                    </Button>
                  )}
                  {isVoice && (
                    <Button tone="custom"
                      onClick={() => setVoiceModalMapping({ m, inst })}
                      className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                    >
                      <Cog6ToothIcon className="h-3 w-3" /> Configure Number
                    </Button>
                  )}
                  <Button tone="custom" onClick={() => onRemove(m.agentInstanceId)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-gray-400">No entry agents configured yet.</p>
      )}

      {/* Voice Config Modal */}
      {voiceModalMapping && savedId && (
        <VoiceConfigModal
          isOpen={true}
          onClose={() => setVoiceModalMapping(null)}
          mapping={voiceModalMapping.m}
          inst={voiceModalMapping.inst}
          ecoId={savedId}
          webhookBase={webhookBase}
          onSaved={async () => { setVoiceModalMapping(null); await onReload(); }}
        />
      )}

      {savedId ? (
        <div className="flex items-end gap-3 flex-wrap pt-2 border-t border-gray-100">
          <div className="flex-1 min-w-52">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Add Entry Agent <span className="text-gray-400 font-normal">(entry type is read from agent settings)</span>
            </label>
            <SelectField value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select agent instance...</option>
              {available.map((a) => {
                const et = a.metadata?.entryType || 'text';
                return <option key={a.id} value={a.id}>{agentLabel(a)} — {ENTRY_TYPE_LABELS[et] || et}</option>;
              })}
            </SelectField>
          </div>
          <Button tone="blue" onClick={handleAdd} disabled={adding}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg disabled:opacity-50 transition-colors">
            <PlusIcon className="h-4 w-4" />{adding ? 'Adding...' : 'Add'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Save the ecosystem first (Setup tab) to add entry agents.
        </p>
      )}
    </div>
  );
};

// ── Functional Agents Tab ─────────────────────────────────────────────────────
const FunctionalAgentsTab: React.FC<{
  savedId: string | null;
  functionalMappings: any[];
  agentInstances: any[];
  onAdd: (id: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}> = ({ savedId, functionalMappings, agentInstances, onAdd, onRemove }) => {
  const [selectedId, setSelectedId] = useState('');
  const [adding, setAdding] = useState(false);
  const ENTRY_TYPES = new Set(['text', 'inbound-voice', 'outbound-voice']);
  const used = new Set(functionalMappings.map((m) => m.agentInstanceId));
  const available = agentInstances.filter((a) => !used.has(a.id) && !ENTRY_TYPES.has(a.metadata?.entryType || ''));

  const handleAdd = async () => {
    if (!selectedId) { toast.error('Select an agent instance'); return; }
    setAdding(true);
    await onAdd(selectedId);
    setSelectedId('');
    setAdding(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Layer 3 — Functional Agents</h2>
        <p className="text-xs text-gray-400 mt-1">
          Handle specific tasks. The orchestrator uses RAG-based intent detection — routing is automatic, no priority needed.
        </p>
      </div>

      {functionalMappings.length > 0 ? (
        <ul className="divide-y divide-gray-100 bg-white rounded-xl border border-gray-200">
          {functionalMappings.map((m) => {
            const inst = m.agentInstance || agentInstances.find((a) => a.id === m.agentInstanceId);
            return (
              <li key={m.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <span className="text-sm font-medium text-gray-800">{agentLabel(inst)}</span>
                  {inst?.agentType?.name && <span className="ml-2 text-xs text-gray-400">({inst.agentType.name})</span>}
                  {!m.isActive && <span className="ml-2 text-xs text-gray-400">(inactive)</span>}
                </div>
                <Button tone="custom" onClick={() => onRemove(m.agentInstanceId)}
                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : <p className="text-sm text-gray-400">No functional agents configured yet.</p>}

      {savedId ? (
        <div className="flex items-end gap-3 flex-wrap pt-2 border-t border-gray-100">
          <div className="flex-1 min-w-52">
            <label className="block text-xs font-medium text-gray-600 mb-1">Add Functional Agent</label>
            <SelectField value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select agent instance...</option>
              {available.map((a) => <option key={a.id} value={a.id}>{agentLabel(a)}</option>)}
            </SelectField>
          </div>
          <Button tone="blue" onClick={handleAdd} disabled={adding}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg disabled:opacity-50 transition-colors">
            <PlusIcon className="h-4 w-4" />{adding ? 'Adding...' : 'Add'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Save the ecosystem first (Setup tab) to add functional agents.
        </p>
      )}
    </div>
  );
};

// ── Playground Tab ────────────────────────────────────────────────────────────
const PlaygroundTab: React.FC<{ ecosystem: any }> = ({ ecosystem }) => {
  const user = getUser();
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string; id?: string }[]>([
    { role: 'assistant', content: 'Hi! How can I help you today?' },
  ]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const hasTextEntry = (ecosystem?.entryAgentMappings ?? []).some(
    (m: any) => (m.agentInstance?.metadata?.entryType || m.entryType) === 'text'
  );

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading || !ecosystem?.id) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    const placeholderId = `ph-${Date.now()}`;
    setMessages((prev) => [...prev, { role: 'assistant', content: '…', id: placeholderId } as any]);
    try {
      const response = await ecosystemService.sendMessageStream({
        ecosystemId: ecosystem.id,
        userId: user?.user?.id || 'playground-user',
        message: msg,
        conversationId,
      });

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts[parts.length - 1];
        for (let i = 0; i < parts.length - 1; i++) {
          const line = parts[i].trim();
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'content') {
              accumulated += data.content;
              setMessages((prev) => prev.map((m: any) =>
                m.id === placeholderId ? { ...m, content: accumulated } : m
              ));
            } else if (data.type === 'complete') {
              const finalContent = data.content || accumulated;
              setMessages((prev) => prev.map((m: any) =>
                m.id === placeholderId ? { ...m, content: finalContent } : m
              ));
              if (data.conversationId) setConversationId(data.conversationId);
            } else if (data.conversationId && !conversationId) {
              setConversationId(data.conversationId);
            }
          } catch { /* skip malformed SSE chunk */ }
        }
      }

      // If nothing accumulated (e.g. non-streaming JSON fallback), parse final buffer
      if (!accumulated && buffer.trim()) {
        try {
          const fallback = JSON.parse(buffer.trim());
          const text = fallback?.message || fallback?.content || fallback?.reply || '';
          if (text) {
            setMessages((prev) => prev.map((m: any) =>
              m.id === placeholderId ? { ...m, content: text } : m
            ));
            if (fallback?.conversationId) setConversationId(fallback.conversationId);
          }
        } catch { /* not JSON */ }
      }

      if (!accumulated) {
        setMessages((prev) => prev.map((m: any) =>
          m.id === placeholderId && m.content === '…' ? { ...m, content: 'No response received.' } : m
        ));
      }
    } catch (e: any) {
      setMessages((prev) => prev.map((m: any) =>
        m.id === placeholderId ? { ...m, content: `Error: ${e?.message || 'Failed to send message'}` } : m
      ));
    } finally {
      setLoading(false);
    }
  };

  if (!ecosystem?.id) {
    return (
      <div className="p-6 text-center text-gray-400 text-sm">
        Save the ecosystem first to use the playground.
      </div>
    );
  }

  if (!hasTextEntry) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-800">
          <h3 className="font-semibold mb-2">Playground requires a Text entry agent</h3>
          <p>This ecosystem only has voice entry agents. The playground is for text (chat) interactions. Add a text entry agent or test voice via Twilio directly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col h-[600px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-700">Ecosystem Playground</h2>
          <p className="text-xs text-gray-400">Testing via text entry — messages go through the full orchestrator routing.</p>
        </div>
        <Button tone="custom" onClick={() => { setMessages([{ role: 'assistant', content: 'Hi! How can I help you today?' }]); setConversationId(undefined); setLoading(false); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
          <ArrowPathIcon className="h-3.5 w-3.5" /> Reset
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-c-f7f9fc rounded-xl border border-gray-200 p-4 space-y-3 mb-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[78%] rounded-xl px-4 py-2.5 text-sm shadow-sm ${
              m.role === 'user'
                ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white'
                : 'bg-white border border-gray-200 text-gray-800'
            }`}>
              {m.role === 'assistant' ? renderChatMarkdown(m.content) : m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <Surface className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-400">
              Thinking…
            </Surface>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message… (Enter to send)"
          rows={2}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
        <Button tone="blue" onClick={send} disabled={loading || !input.trim()}
          className="px-4 py-2 rounded-lg disabled:opacity-50 transition-colors self-end">
          <PaperAirplaneIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ── Voice Config Modal ────────────────────────────────────────────────────────
interface VoiceConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  mapping: any;
  inst: any;
  ecoId: string;
  webhookBase: string;
  onSaved: () => void;
}

const VoiceConfigModal: React.FC<VoiceConfigModalProps> = ({ isOpen, onClose, mapping, inst, ecoId, webhookBase, onSaved }) => {
  const et = mapping?.entryType || inst?.metadata?.entryType || '';
  const existingPhone = (mapping?.config as any)?.twilioPhoneNumber || inst?.metadata?.twilioConfig?.phoneNumber || '';
  const [phoneNumber, setPhoneNumber] = useState(existingPhone);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPhoneNumber((mapping?.config as any)?.twilioPhoneNumber || inst?.metadata?.twilioConfig?.phoneNumber || '');
    }
  }, [isOpen, mapping, inst]);

  if (!isOpen) return null;

  const inboundUrl = (`${webhookBase}/ecosystem/voice/inbound/${ecoId}`).replace("/core/core", "/core");
  const statusUrl = (`${webhookBase}/ecosystem/voice/status/${ecoId}`).replace("/core/core", "/core");

  const handleSave = async () => {
    if (!phoneNumber.trim()) { toast.error('Phone number is required.'); return; }
    if (!phoneNumber.startsWith('+')) { toast.error('Phone number must be in E.164 format (e.g. +14155551234).'); return; }
    setSaving(true);
    try {
      await ecosystemService.updateEntryAgentConfig(ecoId, mapping.agentInstanceId, { twilioPhoneNumber: phoneNumber.trim() });
      toast.success('Voice configuration saved.');
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay tone="dim-40" className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Voice Configuration</h3>
            <p className="text-xs text-gray-500 mt-0.5">{inst?.name || inst?.code || 'Voice Agent'} — {ENTRY_TYPE_LABELS[et] || et}</p>
          </div>
          <Button tone="icon-ghost" onClick={onClose} className="p-1.5 rounded-lg transition-colors">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        <div className="p-5 space-y-5">
          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Twilio Phone Number <span className="text-red-500">*</span>
            </label>
            <TextField
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+14155551234"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              E.164 format. This number is assigned to this ecosystem — not the agent globally.
            </p>
          </div>

          {/* Webhook URLs */}
          {et === 'inbound-voice' && (
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Webhook URLs</h4>
              <p className="text-xs text-gray-500">Set these in your Twilio Console → Phone Numbers → Voice Configuration.</p>
              <div className="space-y-2">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Inbound Call Webhook</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-blue-700 break-all">
                      POST {inboundUrl}
                    </code>
                    <Button tone="custom"
                      onClick={() => copyText(inboundUrl, 'Inbound webhook URL')}
                      className="p-2 border rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">Status Callback</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-blue-700 break-all">
                      POST {statusUrl}
                    </code>
                    <Button tone="custom"
                      onClick={() => copyText(statusUrl, 'Status callback URL')}
                      className="p-2 border rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                <p className="font-medium mb-1">Setup steps:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Enter the Twilio phone number above and save</li>
                  <li>In Twilio Console → <strong>Phone Numbers → Manage → Active Numbers</strong></li>
                  <li>Under <strong>Voice Configuration</strong>, set the webhook to the <em>Inbound Call Webhook</em> above</li>
                  <li>Set the Status Callback URL as well, then save</li>
                </ol>
              </div>
            </div>
          )}

          {et === 'outbound-voice' && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Outbound Calling API</h4>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-blue-700 break-all">
                  POST {webhookBase}/ecosystem/voice/outbound-call
                </code>
                <Button tone="custom"
                  onClick={() => copyText(`POST ${webhookBase}/ecosystem/voice/outbound-call`, 'Outbound API URL')}
                  className="p-2 border rounded-lg hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-400">Body: <code className="bg-gray-100 px-1 rounded">{`{ "ecosystemId": "${ecoId}", "toNumber": "+1..." }`}</code></p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button tone="custom" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </Button>
          <Button tone="blue"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Overlay>
  );
};

// ── Connect Tab ───────────────────────────────────────────────────────────────
const ConnectTab: React.FC<{ ecosystem: any; entryMappings: any[]; agentInstances: any[]; navigate: (path: string) => void }> = ({
  ecosystem, entryMappings, agentInstances, navigate,
}) => {
  const ecoId = ecosystem?.id;
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalValue, setModalValue] = useState('');

  const openModal = (title: string, value: string) => {
    setModalTitle(title);
    setModalValue(value);
    setModalOpen(true);
  };

  const textEntries = entryMappings.filter((m) => {
    const et = m.entryType || 'text';
    return et === 'text';
  });
  const voiceEntries = entryMappings.filter((m) => {
    const et = m.entryType || '';
    return et === 'inbound-voice' || et === 'outbound-voice';
  });

  if (!ecoId) {
    return (
      <div className="p-6 text-sm text-gray-400 text-center">Save the ecosystem first to view connection details.</div>
    );
  }

  // Same tenant resolution as the chatbot Connect tab: the snippets carry the real
  // Client ID when the session has one, and the CLIENT_ID placeholder otherwise.
  const sessionUser = getUser() as any;
  const tenantId = String(
    sessionUser?.tenantId ||
    sessionUser?.user?.tenantId ||
    localStorage.getItem('agentic_tenant_id') ||
    localStorage.getItem('tenant_id') ||
    ''
  );
  const tenantName =
    sessionUser?.tenant?.name ||
    sessionUser?.user?.tenant?.name ||
    sessionUser?.tenantName ||
    sessionUser?.user?.tenantName ||
    '';
  const clientIdToken = tenantId || 'CLIENT_ID';

  // Chat widget codes (for text entry agents)
  const origin = window.location.origin;
  const chatApiUrl = `${API_BASE}/ecosystem/chat/send-message`;
  const directChatUrl = `${origin}/eco-chat/${ecoId}/${clientIdToken}`;
  const iframeCode = `<iframe\n  src="${origin}/eco-chat/${ecoId}/${clientIdToken}"\n  width="100%"\n  height="600"\n  frameborder="0"\n  style="border: none; border-radius: 8px;"\n></iframe>`;
  const floatingCode = `<script>\n  window.AGENTIC_CHAT_CONFIG = { ecosystemId: '${ecoId}', clientId: '${clientIdToken}' };\n  var s = document.createElement('script');\n  s.src = '${origin}/floating-button-widget.js';\n  s.async = true;\n  document.head.appendChild(s);\n</script>`;
  const scriptCode = `<script>\n  window.AGENTIC_CHAT_CONFIG = { ecosystemId: '${ecoId}', clientId: '${clientIdToken}' };\n  var s = document.createElement('script');\n  s.src = '${origin}/widget.js';\n  s.async = true;\n  document.head.appendChild(s);\n</script>`;

  return (
    <div className="p-6 space-y-8">

      <ClientSelectModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        value={modalValue}
        tenantId={tenantId}
        tenantName={tenantName}
      />

      {/* ── Text entry: chat widget ── */}
      {textEntries.length > 0 && (
        <div className="space-y-5">
          <div className="flex items-start gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Chat Widget Integration</h3>
              <p className="text-sm text-gray-600">
                This ecosystem has a <strong>text entry agent</strong>. Embed it on any website as an iframe or floating widget.
                {tenantId
                  ? <>Your Client ID is already filled into every snippet below.</>
                  : <>Replace <code className="bg-white px-1 py-0.5 rounded font-mono text-xs">CLIENT_ID</code> with your actual client ID before use.</>}
              </p>
            </div>
          </div>

          {/* Direct Chat Link */}
          <div className="border rounded-xl p-4 bg-gradient-to-br from-green-50 to-emerald-50">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-sm text-gray-800">Direct Chat Link</h4>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              {tenantId
                ? <>Share this link to open the chat directly.</>
                : <>Replace <code className="bg-white px-1.5 py-0.5 rounded font-mono text-xs">CLIENT_ID</code> with your actual client ID.</>}
            </p>
            <div className="flex items-center gap-2">
              <TextField
                value={directChatUrl}
                readOnly
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-mono"
              />
              <Button tone="custom" onClick={() => openModal('Copy Direct Chat Link', directChatUrl)}
                className="flex items-center gap-1 text-xs px-3 py-1 border rounded-lg hover:bg-white transition-colors text-gray-600">
                <DocumentDuplicateIcon className="h-3.5 w-3.5" /> Copy
              </Button>
            </div>
            <div className="mt-3 p-3 bg-white rounded border">
              <p className="text-xs text-gray-400 mb-1">Example:</p>
              <code className="text-xs font-mono text-blue-600 break-all">
                {origin}/eco-chat/{ecoId}/550e8400-e29b-41d4-a716-446655440001
              </code>
            </div>
          </div>

          {/* API reference */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2">
            <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">API Endpoint</h4>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-gray-200 rounded px-3 py-2 font-mono text-blue-700 break-all">
                POST {chatApiUrl}
              </code>
              <Button tone="custom" onClick={() => copyText(`POST ${chatApiUrl}`, 'Endpoint')}
                className="p-2 text-gray-400 hover:text-gray-700 border rounded-lg hover:bg-white transition-colors">
                <DocumentDuplicateIcon className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-400">Body: <code className="bg-gray-100 px-1 rounded">{`{ "ecosystemId": "${ecoId}", "userId": "...", "message": "..." }`}</code></p>
          </div>

          {/* Iframe */}
          <div className="border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-sm text-gray-800">Iframe Embed (Inline)</h4>
              <Button tone="custom" onClick={() => openModal('Copy Iframe Embed Code', iframeCode)}
                className="flex items-center gap-1 text-xs px-3 py-1 border rounded-lg hover:bg-gray-50 transition-colors">
                <DocumentDuplicateIcon className="h-3.5 w-3.5" /> Copy
              </Button>
            </div>
            <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-40"><code>{iframeCode}</code></pre>
          </div>

          {/* Scripts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-sm text-gray-800">Popup Widget (Floating Button)</h4>
                <Button tone="custom" onClick={() => openModal('Copy Floating Button Script', floatingCode)}
                  className="flex items-center gap-1 text-xs px-3 py-1 border rounded-lg hover:bg-gray-50 transition-colors">
                  <DocumentDuplicateIcon className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-40"><code>{floatingCode}</code></pre>
            </div>
            <div className="border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-sm text-gray-800">Popup Widget</h4>
                <Button tone="custom" onClick={() => openModal('Copy Widget Script', scriptCode)}
                  className="flex items-center gap-1 text-xs px-3 py-1 border rounded-lg hover:bg-gray-50 transition-colors">
                  <DocumentDuplicateIcon className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <pre className="p-3 bg-gray-50 border rounded text-xs overflow-auto max-h-40"><code>{scriptCode}</code></pre>
            </div>
          </div>
        </div>
      )}

      {/* ── Voice entry: Twilio setup ── */}
      {voiceEntries.length > 0 && (
        <div className="space-y-5">
          <div className="flex items-start gap-3 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-5">
            <PhoneIcon className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Voice Integration (Twilio)</h3>
              <p className="text-sm text-gray-600">
                Configure Twilio credentials per agent (Account SID &amp; Auth Token), then configure the phone number and webhook URLs per ecosystem using the <strong>Configure Voice</strong> button below.
              </p>
            </div>
          </div>

          {voiceEntries.map((m) => {
            const inst = m.agentInstance || agentInstances.find((a) => a.id === m.agentInstanceId);
            const et = m.entryType;
            const hasTwilio = !!inst?.metadata?.twilioConfig?.accountSid;
            const hasPhone = !!(m.config as any)?.twilioPhoneNumber;

            return (
              <div key={m.id} className="border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-800">{agentLabel(inst)}</span>
                <Badge className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                  {ENTRY_TYPE_LABELS[et] || et}
                </Badge>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${hasTwilio ? 'bg-green-50 text-green-600 border-green-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                  {hasTwilio ? 'Credentials set' : 'Credentials missing'}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${hasPhone ? 'bg-green-50 text-green-600 border-green-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                  {hasPhone ? `Phone: ${(m.config as any).twilioPhoneNumber}` : 'Phone not set'}
                </span>
                <span className="text-xs text-gray-400 ml-1">— configure credentials &amp; number in the Entry Agents tab</span>
              </div>
            );
          })}
        </div>
      )}

      {entryMappings.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">
          Add entry agents (Entry Agents tab) to see connection details.
        </div>
      )}
    </div>
  );
};

// ── Conversations Tab ─────────────────────────────────────────────────────────
const ConversationsTab: React.FC<{ ecosystem: any }> = ({ ecosystem }) => {
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedConv, setSelectedConv] = useState<any | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const loadList = async () => {
    if (!ecosystem?.id) return;
    setLoadingList(true);
    try {
      const res = await ecosystemService.listConversations(ecosystem.id, { limit: 50 });
      const records = (res as any)?.records ?? [];
      setConversations(records);
      if (records.length > 0) setSelectedId((prev) => prev ?? records[0].id);
    } catch (e: any) { toast.error(e?.message || 'Failed to load conversations'); }
    finally { setLoadingList(false); }
  };

  const loadDetail = async () => {
    if (!selectedId) { setSelectedConv(null); return; }
    setLoadingDetail(true);
    try {
      const conv = await ecosystemService.getConversationById(selectedId);
      const messages = ((conv as any)?.messages ?? [])
        .slice()
        .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((m: any) => ({
          ...m,
          metadata: m.metadata && typeof m.metadata === 'string' ? JSON.parse(m.metadata) : m.metadata,
        }));
      setSelectedConv({ ...conv, messages });
    } catch (e: any) { toast.error(e?.message || 'Failed to load conversation'); }
    finally { setLoadingDetail(false); }
  };

  useEffect(() => { loadList(); }, [ecosystem?.id]);
  useEffect(() => { loadDetail(); }, [selectedId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selectedConv?.messages]);

  return (
    <div className="p-6">
      <div className="grid grid-cols-10 gap-4 min-h-[520px]">
        <aside className="col-span-10 md:col-span-3 bg-gray-50 rounded-lg border p-3 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">Conversations</span>
            </div>
            <span className="text-xs text-gray-400">{loadingList ? '…' : conversations.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2">
            {loadingList ? <div className="text-xs text-gray-400">Loading…</div>
              : conversations.length === 0 ? <div className="text-xs text-gray-400">No conversations yet.</div>
              : conversations.map((c) => (
                <Button tone="custom" key={c.id} onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left p-3 rounded-lg transition-shadow ${c.id === selectedId ? 'bg-white border shadow' : 'hover:bg-white/60'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-medium text-gray-800 truncate">{c.title ?? 'Conversation'}</span>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatDate(c.lastMessageAt ?? c.createdAt)}</span>
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5 truncate">{c.lastMessage ?? '—'}</div>
                  <div className="text-[10px] text-gray-400 mt-1">{c.messageCount ?? 0} messages</div>
                </Button>
              ))}
          </div>
          <Button tone="custom" onClick={loadList} className="mt-3 w-full px-3 py-2 rounded-lg text-xs border bg-white hover:bg-gray-50">Refresh</Button>
        </aside>

        <section className="col-span-10 md:col-span-7 bg-white rounded-lg border flex flex-col">
          <div className="px-4 py-3 border-b">
            <div className="text-sm font-semibold text-gray-800">{selectedConv?.title ?? 'Conversation details'}</div>
            {selectedConv && <div className="text-xs text-gray-400">{selectedConv.messageCount ?? 0} messages · {formatDate(selectedConv.createdAt)}</div>}
          </div>
          <div className="flex-1 h-[460px] overflow-y-auto p-4 bg-c-f7f9fc space-y-3">
            {loadingDetail
              ? <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading…</div>
              : !selectedConv
              ? <div className="flex items-center justify-center h-full text-gray-400 text-sm">Select a conversation from the left.</div>
              : (selectedConv.messages ?? []).length === 0
              ? <div className="flex items-center justify-center h-full text-gray-400 text-sm">No messages.</div>
              : (selectedConv.messages ?? []).map((m: any) => {
                  const isUser = m.senderType === 'user';
                  return (
                    <div key={m.id}>
                      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[78%] rounded-xl px-4 py-2.5 text-sm shadow-sm ${
                          isUser ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-800'
                        }`}>
                          {renderChatMarkdown(m.metadata?.formattedMessage || m.message)}
                        </div>
                      </div>
                      <div className={`text-[10px] text-gray-400 mt-0.5 ${isUser ? 'text-right' : ''}`}>{formatDate(m.createdAt)}</div>
                    </div>
                  );
                })}
            <div ref={endRef} />
          </div>
          <div className="px-4 py-2 border-t text-xs text-gray-400">Read-only conversation history.</div>
        </section>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const EcosystemConfig: React.FC = () => {
  const { ecosystemId } = useParams<{ ecosystemId: string }>();
  const isNew = !ecosystemId || ecosystemId === 'new';
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabId>('setup');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [state, setState] = useState<'draft' | 'published' | 'archived'>('draft');
  const [isActive, setIsActive] = useState(true);
  const [entryMappings, setEntryMappings] = useState<any[]>([]);
  const [functionalMappings, setFunctionalMappings] = useState<any[]>([]);
  const [agentInstances, setAgentInstances] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(isNew ? null : ecosystemId!);
  const [ecosystemData, setEcosystemData] = useState<any>(null);

  useEffect(() => {
    loadAgentInstances();
    if (!isNew) loadEcosystem();
  }, []);

  const loadAgentInstances = async () => {
    try {
      const res = await agentInstanceService.listAgentInstance({ limit: 200 });
      const raw = (res as any)?.agentInstances ?? (res as any)?.items ?? (Array.isArray(res) ? res : []);
      setAgentInstances(raw);
    } catch { /* non-blocking */ }
  };

  const loadEcosystem = async () => {
    setLoading(true);
    try {
      const eco = await ecosystemService.getById(ecosystemId!);
      setName(eco.name);
      setDescription(eco.description ?? '');
      setState(eco.state);
      setIsActive(eco.isActive);
      setEntryMappings(eco.entryAgentMappings ?? []);
      setFunctionalMappings(eco.functionalAgentMappings ?? []);
      setEcosystemData(eco);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load ecosystem');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (isNew && !savedId) {
        const created = await ecosystemService.create({ name: name.trim(), description, state, isActive });
        setSavedId(created.id);
        setEcosystemData(created);
        toast.success('Ecosystem created');
        navigate(`/ecosystems/${created.id}/config`, { replace: true });
      } else {
        const updated = await ecosystemService.update(savedId!, { name: name.trim(), description, state, isActive });
        setEcosystemData(updated);
        toast.success('Ecosystem saved');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddEntry = async (agentInstanceId: string) => {
    if (!savedId) return;
    const inst = agentInstances.find((a) => a.id === agentInstanceId);
    const entryType = inst?.metadata?.entryType || 'text';
    try {
      await ecosystemService.addEntryAgent(savedId, { agentInstanceId, entryType, isActive: true });
      toast.success('Entry agent added');
      await loadEcosystem();
    } catch (e: any) { toast.error(e?.message || 'Failed to add entry agent'); }
  };

  const handleRemoveEntry = async (agentInstanceId: string) => {
    if (!savedId) return;
    try {
      await ecosystemService.removeEntryAgent(savedId, agentInstanceId);
      toast.success('Entry agent removed');
      await loadEcosystem();
    } catch (e: any) { toast.error(e?.message || 'Failed to remove entry agent'); }
  };

  const handleAddFunctional = async (agentInstanceId: string) => {
    if (!savedId) return;
    try {
      await ecosystemService.addFunctionalAgent(savedId, { agentInstanceId, isActive: true, priority: 0 });
      toast.success('Functional agent added');
      await loadEcosystem();
    } catch (e: any) { toast.error(e?.message || 'Failed to add functional agent'); }
  };

  const handleRemoveFunctional = async (agentInstanceId: string) => {
    if (!savedId) return;
    try {
      await ecosystemService.removeFunctionalAgent(savedId, agentInstanceId);
      toast.success('Functional agent removed');
      await loadEcosystem();
    } catch (e: any) { toast.error(e?.message || 'Failed to remove functional agent'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-agentic-primary-blue" />
      </div>
    );
  }

  const ecoForTabs = ecosystemData ?? (savedId ? { id: savedId, entryAgentMappings: entryMappings, functionalAgentMappings: functionalMappings } : null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Button tone="muted-text" onClick={() => navigate('/ecosystems')}
              className="p-2 transition-colors">
              <ArrowLeftIcon className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-transparent">
                {isNew ? 'New Ecosystem' : (name || 'Ecosystem Config')}
              </h1>
              <p className="text-sm text-agentic-text-secondary mt-0.5">
                Entry agents → Orchestrator (RAG) → Functional agents
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Nav */}
        <Tabs variant="underline-icon" className="mb-6" items={TABS} value={activeTab} onChange={(id) => setActiveTab(id as typeof activeTab)} />

        {/* Tab Content */}
        <Surface className="bg-white rounded-xl border shadow-sm">
          {activeTab === 'setup' && (
            <SetupTab
              name={name} setName={setName}
              description={description} setDescription={setDescription}
              state={state} setState={setState}
              isActive={isActive} setIsActive={setIsActive}
              onSave={handleSave} saving={saving}
              isNew={isNew} hasSavedId={!!savedId}
            />
          )}
          {activeTab === 'entry-agents' && (
            <EntryAgentsTab
              savedId={savedId}
              entryMappings={entryMappings}
              agentInstances={agentInstances}
              onAdd={handleAddEntry}
              onRemove={handleRemoveEntry}
              onReload={loadEcosystem}
              navigate={navigate}
            />
          )}
          {activeTab === 'functional-agents' && (
            <FunctionalAgentsTab
              savedId={savedId}
              functionalMappings={functionalMappings}
              agentInstances={agentInstances}
              onAdd={handleAddFunctional}
              onRemove={handleRemoveFunctional}
            />
          )}
          {activeTab === 'playground' && (
            <PlaygroundTab ecosystem={{ ...ecoForTabs, entryAgentMappings: entryMappings }} />
          )}
          {activeTab === 'connect' && (
            <ConnectTab
              ecosystem={ecoForTabs}
              entryMappings={entryMappings}
              agentInstances={agentInstances}
              navigate={navigate}
            />
          )}
          {activeTab === 'conversations' && (
            <ConversationsTab ecosystem={ecoForTabs} />
          )}
        </Surface>
      </div>
    </div>
  );
};

export default EcosystemConfig;
